/**
 * Botão de EXPORT (backup) do tenant.
 *
 * Antes lia o Firestore direto do navegador — o que dava
 * "Missing or insufficient permissions" quando o master tentava exportar
 * outro tenant (as regras barram leitura cross-tenant client-side).
 *
 * Agora chama a Cloud Function `exportarBackup`, que roda com Admin SDK,
 * valida permissão (master ou settings.write do próprio tenant) e gera o
 * JSON no servidor, devolvendo uma signed URL. Baixamos a partir dela.
 */
import { useState } from 'react'
import { Download, Check, AlertCircle, Loader2 } from 'lucide-react'
import { exportarBackup } from '@/services/functions'
import type { Tenant } from '@/types'
import { hojeISO } from '@/utils/date'

interface Props { tenant: Pick<Tenant, 'id' | 'nome_fantasia'> }

export function BackupButton({ tenant }: Props) {
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null)

  async function exportar() {
    setBusy(true)
    setFeedback(null)
    try {
      const { data } = await exportarBackup({ tenantId: tenant.id })
      if (!data.content) throw new Error('Servidor não retornou o conteúdo do backup.')

      const slug = (tenant.nome_fantasia || tenant.id).toLowerCase().replace(/[^a-z0-9]/g, '-')
      const filename = data.filename
        ? `backup-${slug}-${data.filename}`
        : `backup-${slug}-${hojeISO()}.json`

      // O conteúdo vem direto da Function (sem signed URL → sem dependência
      // de IAM de signing). Baixamos como arquivo via blob.
      const blob = new Blob([data.content], { type: 'application/json' })
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = filename
      a.click()
      URL.revokeObjectURL(objUrl)
      setFeedback({ ok: true, msg: `Backup gerado e baixado (${(blob.size / 1024).toFixed(0)} KB).` })
    } catch (e) {
      setFeedback({ ok: false, msg: traduzErro((e as Error).message) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <button
        onClick={exportar}
        disabled={busy}
        className="text-xs font-bold px-3 py-2 rounded-lg bg-accent/15 text-accent
                   hover:bg-accent/25 flex items-center gap-1 disabled:opacity-50"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        {busy ? 'Gerando backup…' : 'Baixar backup completo (.json)'}
      </button>
      {feedback && (
        <div className={`text-xs flex items-center gap-1 ${feedback.ok ? 'text-success' : 'text-danger'}`}>
          {feedback.ok ? <Check size={12} /> : <AlertCircle size={12} />}
          {feedback.msg}
        </div>
      )}
    </div>
  )
}

function traduzErro(msg: string): string {
  if (/permission|insufficient|denied/i.test(msg)) {
    return 'Sem permissão pra exportar este perfil. Apenas o master ou o dono (com permissão de configurações) pode.'
  }
  if (/unauthenticated|login/i.test(msg)) return 'Sessão expirada. Faça login novamente.'
  return msg
}
